/* eslint-disable react-refresh/only-export-components -- pure art module: it
   exports palettes, pattern functions and a recipe, never a component. */
import type { ReactNode } from 'react';

import type { AssignmentReason } from '@/api/curate';

/**
 * The pure half of Curate's generative cover art: palettes, the eight pattern
 * families, and the recipe function that maps an article to one of each.
 * No component lives here so React Fast Refresh can treat `CurateCover.tsx`
 * as a pure component module.
 */

/** Brand-derived gradient pairs. Hex is deliberate: this is artwork, not UI chrome. */
export const PALETTES: ReadonlyArray<readonly [string, string]> = [
  ['#0f4d52', '#002b2a'], // deep teal -> foundation
  ['#2f8a93', '#14424c'], // glacier -> sage
  ['#235e6e', '#0b323c'], // tidal -> dusk
  ['#8dbcb8', '#2d7c85'], // stone green -> glacier (light, airy)
  ['#1b5368', '#061f26'], // slate teal -> near black
  ['#124c5c', '#0a3f3d'], // sage -> deep green
];

/** The lit element in every cover, keyed by why the article was assigned. */
export const REASON_ACCENT: Record<AssignmentReason, string> = {
  complaint_deficit: '#ff8a75', // Fuel Orange
  indicator_deficit: '#63e6be', // Mint
  manual: '#cfe3df', // Pale stone
  tag_rule: '#f7a66a', // Amber
};

export const PATTERN_KEYS = [
  'topo',
  'orbit',
  'bloom',
  'facets',
  'pulse',
  'lattice',
  'halftone',
  'arcs',
] as const;
export type PatternKey = (typeof PATTERN_KEYS)[number];

/** Families whose interest sits on the right, safe under the hero's left-side scrim. */
const HERO_PATTERNS: readonly PatternKey[] = ['orbit', 'bloom', 'facets', 'halftone', 'topo'];

/** FNV-1a, 32-bit. Stable across runtimes, which is the whole point. */
export function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

export interface CoverRecipe {
  pattern: PatternKey;
  palette: readonly [string, string];
  accent: string;
}

export interface CoverInputs {
  slug: string;
  tag?: string | null;
  reason?: AssignmentReason;
  /** Hero covers draw from the right-weighted families only. */
  hero?: boolean;
}

/** Pure: the same inputs always yield the same recipe. Exported for tests. */
export function coverRecipe({ slug, tag, reason, hero }: CoverInputs): CoverRecipe {
  const h = hashString(slug);
  const families = hero ? HERO_PATTERNS : PATTERN_KEYS;
  return {
    pattern: families[h % families.length],
    palette: PALETTES[hashString(tag || slug) % PALETTES.length],
    accent: (reason && REASON_ACCENT[reason]) || REASON_ACCENT.indicator_deficit,
  };
}

/* Pattern families
 * Each draws into a 100x100 box and lights exactly one element in the accent.
 * -------------------------------------------------------------------------- */

const r1 = (n: number) => Math.round(n * 10) / 10;

export const PATTERNS: Record<PatternKey, (accent: string) => ReactNode> = {
  /* Contour lines, like a topographic map. */
  topo: (a) => (
    <g fill="none" strokeLinecap="round">
      {Array.from({ length: 8 }, (_, i) => {
        const y = 92 - i * 11;
        const lit = i === 3;
        return (
          <path
            key={i}
            d={`M-6 ${y} C 18 ${y - 14}, 34 ${y + 16}, 56 ${y - 4} S 90 ${y - 18}, 108 ${y - 6}`}
            stroke={lit ? a : '#fff'}
            strokeOpacity={lit ? 0.95 : 0.07 + i * 0.025}
            strokeWidth={lit ? 2.2 : 1.4}
          />
        );
      })}
    </g>
  ),

  /* Concentric orbits with one accent arc and a glowing satellite. */
  orbit: (a) => {
    const cx = 70;
    const cy = 64;
    const r = 34;
    const ang = -0.55;
    const px = r1(cx + r * Math.cos(ang));
    const py = r1(cy + r * Math.sin(ang));
    return (
      <>
        <g fill="none" stroke="#fff" strokeWidth={1.4}>
          <circle cx={cx} cy={cy} r={16} strokeOpacity={0.14} />
          <circle cx={cx} cy={cy} r={r} strokeOpacity={0.12} />
          <circle cx={cx} cy={cy} r={52} strokeOpacity={0.1} />
          <circle cx={cx} cy={cy} r={70} strokeOpacity={0.07} />
        </g>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={a}
          strokeOpacity={0.9}
          strokeWidth={2.2}
          strokeDasharray="74 140"
          strokeLinecap="round"
          transform={`rotate(-150 ${cx} ${cy})`}
        />
        <circle cx={px} cy={py} r={9} fill={a} fillOpacity={0.28} />
        <circle cx={px} cy={py} r={4} fill={a} />
      </>
    );
  },

  /* Six translucent petals around a lit centre. */
  bloom: (a) => (
    <>
      {Array.from({ length: 6 }, (_, i) => (
        <ellipse
          key={i}
          cx={58}
          cy={56}
          rx={30}
          ry={12}
          fill="#fff"
          fillOpacity={0.11}
          transform={`rotate(${i * 30} 58 56) translate(14 0)`}
        />
      ))}
      <circle cx={58} cy={56} r={13} fill={a} fillOpacity={0.25} />
      <circle cx={58} cy={56} r={6.5} fill={a} />
    </>
  ),

  /* Overlapping translucent planes, one in the accent. */
  facets: (a) => (
    <>
      <polygon points="0,100 100,0 100,100" fill="#fff" fillOpacity={0.07} />
      <polygon points="0,100 100,38 100,100" fill="#000" fillOpacity={0.14} />
      <polygon points="34,100 100,58 100,100" fill={a} fillOpacity={0.42} />
      <polygon points="0,56 60,100 0,100" fill="#fff" fillOpacity={0.1} />
      <polygon points="0,0 46,0 0,38" fill="#fff" fillOpacity={0.06} />
    </>
  ),

  /* Faint grid with a glowing trace across it. */
  pulse: (a) => {
    const d = 'M-6 56 H 24 L 33 42 L 41 74 L 49 28 L 58 64 L 65 56 H 108';
    return (
      <>
        <g stroke="#fff" strokeOpacity={0.07} strokeWidth={1}>
          {[12.5, 25, 37.5, 50, 62.5, 75, 87.5].map((i) => (
            <path key={i} d={`M${i} 0V100M0 ${i}H100`} />
          ))}
        </g>
        <path
          d={d}
          fill="none"
          stroke={a}
          strokeOpacity={0.28}
          strokeWidth={8}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={d}
          fill="none"
          stroke={a}
          strokeWidth={2.4}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </>
    );
  },

  /* Isometric lattice fading toward the top-left, accent on two cells. */
  lattice: (a) => (
    <>
      {Array.from({ length: 64 }, (_, n) => {
        const r = Math.floor(n / 8);
        const c = n % 8;
        const x = c * 15 + (r % 2 ? 7.5 : 0) - 4;
        const y = r * 9 + 6;
        const t = (x + y) / 190;
        const lit = (r === 4 && c === 5) || (r === 5 && c === 4);
        return (
          <path
            key={n}
            d={`M${x} ${y}l7.5 4.5-7.5 4.5-7.5-4.5z`}
            fill={lit ? a : '#fff'}
            fillOpacity={lit ? 0.9 : 0.03 + t * 0.26}
          />
        );
      })}
    </>
  ),

  /* Halftone dots swelling toward the bottom-right corner. */
  halftone: (a) => {
    const steps = Array.from({ length: 9 }, (_, i) => 8 + i * 11.5);
    return (
      <>
        {steps.flatMap((y) =>
          steps.map((x) => {
            const d = Math.hypot(100 - x, 100 - y);
            const lit = d < 26;
            return (
              <circle
                key={`${x}-${y}`}
                cx={x}
                cy={y}
                r={r1(Math.max(0.6, 4.6 - d / 26))}
                fill={lit ? a : '#fff'}
                fillOpacity={lit ? 0.95 : 0.08 + (1 - d / 140) * 0.3}
              />
            );
          }),
        )}
      </>
    );
  },

  /* Stacked quarter arcs rising from the bottom-left, one lit. */
  arcs: (a) => (
    <g fill="none" strokeLinecap="round">
      {Array.from({ length: 7 }, (_, i) => {
        const r = 16 + i * 14;
        const lit = i === 2;
        return (
          <path
            key={i}
            d={`M0 ${100 - r} A ${r} ${r} 0 0 1 ${r} 100`}
            stroke={lit ? a : '#fff'}
            strokeOpacity={lit ? 0.95 : 0.2 - i * 0.02}
            strokeWidth={lit ? 3 : 1.8}
          />
        );
      })}
    </g>
  ),
};
